import AppButton from '@/components/ui/AppButton';
import AppInput from '@/components/ui/AppInput';
import AppPhoneInput from '@/components/ui/AppPhoneInput';
import AppRadioButton from '@/components/ui/AppRadioButton';
import FormErrorMessage from '@/components/ui/FormErrorMessage';
import { endpoints } from '@/services/endpoints';
import { axiosPostRequest, axiosPatchRequest } from '@/services/Request';
import { isOptionalPhoneValid } from '@/utils/phoneUtils';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { forwardRef } from 'react';
import { Controller, FieldValues, SubmitHandler, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import * as z from 'zod';
import { X, UserPlus } from 'lucide-react';

const addUserSchema = z.object({
  fullName: z.string().min(2, 'Full name is required (min 2 characters)'),
  email: z.string().email('Please enter a valid email').optional().or(z.literal('')),
  phoneNumber: z
    .string()
    .optional()
    .or(z.literal(''))
    .refine(isOptionalPhoneValid, 'Please enter a valid phone number'),
  gender: z.enum(['male', 'female'], { required_error: 'Gender is required' }),
  role: z.enum(['user', 'admin', 'editor'], { required_error: 'Role is required' }),
  password: z.string().min(6, 'Password must be at least 6 characters'),
  birthDate: z.string().optional(),
  isPhoneNumberVerified: z.boolean().default(false),
});

type AddUserForm = z.infer<typeof addUserSchema>;

const AddUserModal = forwardRef<HTMLDialogElement>((_, ref) => {
  const queryClient = useQueryClient();

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    control,
    formState: { errors },
  } = useForm<AddUserForm>({
    resolver: zodResolver(addUserSchema),
    defaultValues: {
      isPhoneNumberVerified: false,
    },
  });

  const { mutate: createUser, isPending } = useMutation({
    mutationFn: async (data: FieldValues) => {
      // Step 1: Create user via signUp
      const { role, ...signUpPayload } = data;
      const res = await axiosPostRequest('api/v2/auth/signUp', signUpPayload);
      // Step 2: If non-user role requested, update role via admin endpoint
      if (role && role !== 'user' && res?.result?._id) {
        await axiosPatchRequest(endpoints.adminUpdateUserProfile, { role }, { id: res.result._id });
      }
      return res;
    },
    onSuccess: () => {
      toast.success('User created successfully', { style: { zIndex: 90000 } });
      queryClient.invalidateQueries({ queryKey: [endpoints.getAllUsers] });
      reset();
      (ref as React.RefObject<HTMLDialogElement>)?.current?.close();
    },
    onError: (error: any) => {
      toast.error(typeof error === 'string' ? error : error?.message || 'Failed to create user', {
        style: { zIndex: 90000 },
      });
    },
  });

  const onSubmit: SubmitHandler<FieldValues> = data => {
    const payload: Record<string, any> = { ...data };
    if (!payload.email) delete payload.email;
    if (!payload.phoneNumber) delete payload.phoneNumber;
    if (!payload.birthDate) delete payload.birthDate;
    createUser(payload);
  };

  return (
    <dialog
      ref={ref}
      className="fixed inset-0 z-50 m-auto w-full max-w-md rounded-xl border border-coal-200 bg-white p-0 shadow-dropdown backdrop:bg-coal-900/40 backdrop:backdrop-blur-sm"
    >
      <div className="flex items-center justify-between border-b border-coal-200 px-6 py-4">
        <div className="flex items-center gap-2">
          <UserPlus className="h-5 w-5 text-primary" />
          <h2 className="text-lg font-semibold text-coal-900">Add User</h2>
        </div>
        <form method="dialog">
          <button
            type="submit"
            className="rounded-md p-1 text-coal-400 hover:bg-coal-100 hover:text-coal-600"
          >
            <X className="h-5 w-5" />
          </button>
        </form>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col">
        <div className="max-h-[calc(100svh-14rem)] space-y-4 overflow-auto px-6 py-4">
          {/* Full Name */}
          <div>
            <label htmlFor="fullName" className="mb-1.5 block text-sm font-medium text-coal-700">
              Full Name <span className="text-red-500">*</span>
            </label>
            <AppInput
              id="fullName"
              type="text"
              placeholder="Enter full name"
              {...register('fullName')}
            />
            <FormErrorMessage message={errors.fullName?.message} />
          </div>

          {/* Email */}
          <div>
            <label htmlFor="email" className="mb-1.5 block text-sm font-medium text-coal-700">
              Email
            </label>
            <AppInput
              id="email"
              type="email"
              placeholder="user@example.com"
              {...register('email')}
            />
            <FormErrorMessage message={errors.email?.message} />
          </div>

          {/* Phone */}
          <div>
            <label htmlFor="phoneNumber" className="mb-1.5 block text-sm font-medium text-coal-700">
              Phone Number
            </label>
            <Controller
              name="phoneNumber"
              control={control}
              render={({ field }) => (
                <AppPhoneInput
                  id="phoneNumber"
                  value={field.value ?? ''}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  placeholder="10 9398 4420"
                />
              )}
            />
            <FormErrorMessage message={errors.phoneNumber?.message} />
          </div>

          {/* Phone Verification */}
          <label className="flex cursor-pointer items-center gap-2 text-sm text-coal-700">
            <input
              type="checkbox"
              className="h-4 w-4 rounded border-coal-300 text-primary focus:ring-primary"
              {...register('isPhoneNumberVerified')}
            />
            Phone number is verified
          </label>

          {/* Password */}
          <div>
            <label htmlFor="password" className="mb-1.5 block text-sm font-medium text-coal-700">
              Password <span className="text-red-500">*</span>
            </label>
            <AppInput
              id="password"
              type="password"
              placeholder="Min 6 characters"
              {...register('password')}
            />
            <FormErrorMessage message={errors.password?.message} />
          </div>

          {/* Gender */}
          <div>
            <p className="mb-1.5 text-sm font-medium text-coal-700">
              Gender <span className="text-red-500">*</span>
            </p>
            <div className="flex gap-5">
              <div className="flex items-center gap-2">
                <AppRadioButton
                  value="male"
                  type="radio"
                  id="gender-male"
                  {...register('gender')}
                  onChange={e => setValue('gender', e.target.value as 'male' | 'female')}
                />
                <label htmlFor="gender-male" className="cursor-pointer text-sm text-coal-600">
                  Male
                </label>
              </div>
              <div className="flex items-center gap-2">
                <AppRadioButton
                  value="female"
                  type="radio"
                  id="gender-female"
                  {...register('gender')}
                  onChange={e => setValue('gender', e.target.value as 'male' | 'female')}
                />
                <label htmlFor="gender-female" className="cursor-pointer text-sm text-coal-600">
                  Female
                </label>
              </div>
            </div>
            <FormErrorMessage message={errors.gender?.message} />
          </div>

          {/* Role */}
          <div>
            <label htmlFor="role" className="mb-1.5 block text-sm font-medium text-coal-700">
              Role <span className="text-red-500">*</span>
            </label>
            <select
              {...register('role')}
              className="h-9 w-full rounded-lg border border-coal-300 bg-white px-3 text-sm text-coal-900 transition-colors hover:border-coal-400 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
            >
              <option value="">Select a role</option>
              <option value="user">User</option>
              <option value="admin">Admin</option>
              <option value="editor">Editor</option>
            </select>
            <FormErrorMessage message={errors.role?.message} />
          </div>

          {/* Birth Date */}
          <div>
            <label htmlFor="birthDate" className="mb-1.5 block text-sm font-medium text-coal-700">
              Birth Date
            </label>
            <AppInput id="birthDate" type="date" {...register('birthDate')} />
            <FormErrorMessage message={errors.birthDate?.message} />
          </div>
        </div>

        {/* Submit */}
        <div className="border-t border-coal-200 px-6 py-4">
          <AppButton loading={isPending} size="md" type="submit" fullWidth>
            Create User
          </AppButton>
        </div>
      </form>
    </dialog>
  );
});

AddUserModal.displayName = 'AddUserModal';

export default AddUserModal;
